import { useQuery, QueryKey, keepPreviousData } from '@tanstack/react-query';
import { normalizeParams, Param } from '@/lib/query/normalizeParams';
import axiosInstance from '@/lib/http/hypertrackerHttpRequest';
import axiosInstancePayment from '@/lib/http/paymentServiseHttpRequests';
import { queryClient } from '@/lib/query/queryClient';
import { clearJsonEtag, fetchJsonWithETag, NO_CHANGE } from '@/lib/http/fetchJsonWithEtag';
import {
    updateApiCache,
    invalidateFetcherApiByPath,
    invalidateAllFetcherApiByPath,
} from './fetcherCache';

export type FetcherParams = Record<string, Param>;
export type FetcherMode = 'auto' | 'json' | 'api';

type Base = {
    staleTimeMs?: number;
    gcTimeMs?: number;
    enabled?: boolean;
    refetchOnWindowFocus?: boolean;
};

/** JSON (no params) */
export function useQueryFetcher<TData = unknown>(
    options: {
        mode?: 'json' | 'auto';
        isAuthorized?: boolean;
        buildJsonUrl: () => string;
        params?: undefined;
    } & Base,
): ReturnType<typeof useQuery<TData>>;

/** JSON (with params) */
export function useQueryFetcher<TData = unknown, TParams extends FetcherParams = FetcherParams>(
    options: {
        mode?: 'json' | 'auto';
        isAuthorized?: boolean; // used only when mode === 'auto'
        buildJsonUrl: (params: TParams) => string;
        params: TParams;
    } & Base,
): ReturnType<typeof useQuery<TData>>;

/** Auto (with params) */
export function useQueryFetcher<TData = unknown, TParams extends FetcherParams = FetcherParams>(
    options: {
        mode?: 'auto';
        isAuthorized?: boolean; // used only when mode === 'auto'
        buildJsonUrl: (params: TParams) => string;
        apiPath: string;
        axiosInstance?: string;
        params: TParams;
    } & Base,
): ReturnType<typeof useQuery<TData>>;

/** Auto (no params) — uses API when authorized, JSON fallback otherwise */
export function useQueryFetcher<TData = unknown>(
    options: {
        mode: 'auto';
        isAuthorized: boolean;
        apiPath: string;
        buildJsonUrl: () => string;
        axiosInstance?: string;
        params?: undefined;
    } & Base,
): ReturnType<typeof useQuery<TData>>;

/** API-only (no params) — does NOT fetch if not authorized; no JSON fallback */
export function useQueryFetcher<TData = unknown>(
    options: {
        mode: 'api';
        isAuthorized: boolean;
        apiPath: string;
        axiosInstance?: string;
        params?: undefined;
    } & Base,
): ReturnType<typeof useQuery<TData>>;

/** API-only (with params) — does NOT fetch if not authorized; no JSON fallback */
export function useQueryFetcher<TData = unknown, TParams extends FetcherParams = FetcherParams>(
    options: {
        mode: 'api';
        isAuthorized: boolean;
        apiPath: string;
        params?: TParams;
        axiosInstance?: string;
    } & Base,
): ReturnType<typeof useQuery<TData>>;

export function useQueryFetcher<TData = unknown, TParams extends FetcherParams = FetcherParams>(
    options:
        | (Base & {
              mode?: 'json' | 'auto';
              isAuthorized?: boolean;
              buildJsonUrl: ((p: TParams) => string) | (() => string);
              axiosInstance?: string;
              params?: TParams;
          })
        | (Base & {
              mode: 'api';
              isAuthorized: boolean;
              apiPath: string;
              axiosInstance?: string;
              params?: TParams;
          }),
) {
    const {
        staleTimeMs = 60_000,
        gcTimeMs = 60 * 60 * 1000,
        enabled: userEnabled = true,
        refetchOnWindowFocus = false,
    } = options as Base;

    const mode = (options as any).mode ?? 'auto';

    const https = options.axiosInstance === 'payment' ? axiosInstancePayment : axiosInstance;

    let resolvedMode: FetcherMode = mode as FetcherMode;
    let identity = '';
    let hasParams = false;
    let params: TParams | undefined;
    const isAuthorized = (options as any).isAuthorized ?? false;
    let apiPath: string | undefined;
    let buildJsonUrl: any;

    if (mode === 'api') {
        resolvedMode = 'api';
        apiPath = (options as any).apiPath;
        if (!apiPath) throw new Error('useQueryFetcher(api): apiPath is required');
        params = (options as any).params;
        hasParams = typeof params !== 'undefined';
        identity = apiPath;
    } else {
        buildJsonUrl = (options as any).buildJsonUrl;
        params = (options as any).params;
        hasParams = typeof params !== 'undefined';
        if (mode === 'auto') {
            resolvedMode = isAuthorized ? 'api' : 'json';
        } else {
            resolvedMode = 'json';
        }
        identity =
            resolvedMode === 'json'
                ? hasParams
                    ? (buildJsonUrl as (p: TParams) => string)(params as TParams)
                    : (buildJsonUrl as () => string)()
                : ((options as any).apiPath as string); // if you ever set auto+auth
    }

    const keyParts: any[] = ['fetcher', resolvedMode, identity];
    if (hasParams) keyParts.push(normalizeParams(params as TParams));
    const queryKey: QueryKey = keyParts;

    const effectiveEnabled =
        mode === 'api' ? Boolean(userEnabled && isAuthorized) : Boolean(userEnabled);

    return useQuery<TData>({
        queryKey,
        queryFn: async ({ signal }) => {
            if (resolvedMode === 'api') {
                // at this point mode is api; if not authorized we shouldn't be here because enabled=false
                const response = await https.get<TData>(identity, {
                    params: hasParams ? params : undefined,
                    signal,
                });
                return response.data;
            } else {
                const url = identity;
                const previous = queryClient.getQueryData<TData>(queryKey);
                if (!previous) clearJsonEtag(url); // avoid 304-without-cache edge
                const result = await fetchJsonWithETag<TData>(url, { signal });
                return result === NO_CHANGE ? (previous as TData) : result;
            }
        },
        staleTime: staleTimeMs,
        gcTime: gcTimeMs,
        refetchOnWindowFocus: refetchOnWindowFocus,
        refetchOnReconnect: false,
        retry: 1,
        placeholderData: keepPreviousData,
        structuralSharing: true,
        enabled: effectiveEnabled,
    });
}

export function invalidateFetcherJsonByFilename(fileName: string) {
    queryClient.invalidateQueries({
        predicate(q) {
            const k = q.queryKey as any[];
            const isJson = k[0] === 'fetcher' && k[1] === 'json';
            if (!isJson) return false;
            const url = String(k[2] ?? '');
            const hit = url.endsWith(fileName);
            if (hit) clearJsonEtag(url);
            return hit;
        },
        refetchType: 'active',
    });
}

export function invalidateFetcherJsonByUrl(url: string) {
    clearJsonEtag(url);
    queryClient.invalidateQueries({
        predicate(q) {
            const k = q.queryKey as any[];
            return k[0] === 'fetcher' && k[1] === 'json' && String(k[2] ?? '') === url;
        },
        refetchType: 'active',
    });
}

export { updateApiCache, invalidateFetcherApiByPath, invalidateAllFetcherApiByPath };
